/**
 * Wishlist Button Web Component
 *
 * Custom element for wishlist/favorite functionality.
 * Works with WishlistState for localStorage + server sync.
 *
 * Like count display logic:
 * - For logged-in users: server like_count is authoritative (includes their like)
 * - For guests: server like_count + 1 if they have it liked locally
 *   (because guest likes don't update the server count)
 *
 * Usage:
 *   <wishlist-button
 *     data-product-handle="product-handle"
 *     data-product-id="123"
 *     data-like-count="42"
 *     data-product-image="https://..."
 *   ></wishlist-button>
 *
 * Attributes:
 *   - data-product-handle: Product handle (required)
 *   - data-product-id: Product ID for like count updates
 *   - data-like-count: Current like count (for display)
 *   - data-product-image: Product image URL for fly animation
 *   - data-compact: Use compact styling (cards)
 */

import WishlistState from "./wishlist-state.js";

/**
 * Calculates a point on a cubic Bézier curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

/**
 * FlyToWishlist - Animates product image flying to the header wishlist icon
 */
class FlyToWishlist extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };
    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };
    // Control points create the swoop arc (matching cart animation)
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        setTimeout(() => this.remove(), 300);
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    requestAnimationFrame(animate);
  }
}

if (!customElements.get('fly-to-wishlist')) {
  customElements.define('fly-to-wishlist', FlyToWishlist);
}

class WishlistButton extends HTMLElement {
  static get observedAttributes() {
    return ["data-product-handle", "data-like-count"];
  }

  constructor() {
    super();
    this.unsubscribe = null;
    this.isAnimating = false;
  }

  connectedCallback() {
    this.productHandle = this.dataset.productHandle;
    this.productId = this.dataset.productId || null;
    this.productImage = this.dataset.productImage || null;
    // Store original server count - this never changes unless page reloads
    this.serverLikeCount = parseInt(this.dataset.likeCount || "0", 10);
    this.isCompact = this.hasAttribute("data-compact");

    this.render();
    this.attachListeners();

    // Subscribe to wishlist state changes
    this.unsubscribe = WishlistState.subscribe(() => {
      this.updateState();
      this.updateCountDisplay();
    });

    this.updateState();
    this.updateCountDisplay();
  }

  disconnectedCallback() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-product-handle" && oldValue !== newValue) {
      this.productHandle = newValue;
      this.updateState();
      this.updateCountDisplay();
    }
    if (name === "data-like-count" && oldValue !== newValue) {
      this.serverLikeCount = parseInt(newValue || "0", 10);
      this.updateCountDisplay();
    }
  }

  /**
   * Humanize like count (e.g., 2334 -> 2.3k)
   */
  humanizeCount(count) {
    if (count >= 1000) {
      const k = count / 1000;
      return k >= 10 ? `${Math.round(k)}k` : `${k.toFixed(1)}k`;
    }
    return String(count);
  }

  /**
   * Calculate the display count based on server count and local state.
   * Optimistically reflects the user's current like state so the badge moves
   * with the heart on every toggle (the previous logic only +1'd guests on
   * the way in and ignored toggles back off; logged-in users never changed
   * at all — the server count was frozen at page-load).
   */
  getDisplayCount() {
    if (!WishlistState.storageLoaded) {
      return this.serverLikeCount;
    }
    const isLiked = WishlistState.isLiked(this.productHandle);
    const isLoggedIn = WishlistState.isLoggedIn;

    if (isLoggedIn) {
      // Server count was rendered with the user's then-current like state.
      // If they've toggled since page load, mirror that delta in the count.
      if (this._initialLikedSnapshot === undefined) {
        this._initialLikedSnapshot = isLiked;
      }
      const delta = (isLiked === this._initialLikedSnapshot) ? 0
                  : (isLiked ? 1 : -1);
      return Math.max(0, this.serverLikeCount + delta);
    }

    // Guests: server count never tracked their like — +1 while currently liked.
    return isLiked ? this.serverLikeCount + 1 : this.serverLikeCount;
  }

  /**
   * Render the button HTML
   */
  render() {
    const countDisplay = this.humanizeCount(this.serverLikeCount);
    const countActiveDisplay = this.humanizeCount(this.serverLikeCount + 1);

    this.innerHTML = `
      <div class="wishlist-button__inner">
        <button
          type="button"
          class="wishlist-button__btn"
          aria-label="Add to wishlist"
        >
          <svg class="wishlist-button__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </button>
        <span
          class="wishlist-button__count"
          data-count="${countDisplay}"
          data-count-active="${countActiveDisplay}"
        ></span>
      </div>
    `;

    this.btn = this.querySelector(".wishlist-button__btn");
    this.countEl = this.querySelector(".wishlist-button__count");
    this.iconEl = this.querySelector(".wishlist-button__icon");
  }

  /**
   * Attach click listeners
   */
  attachListeners() {
    this.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.handleClick();
    });
  }

  /**
   * Handle button click
   */
  async handleClick() {
    if (this.isAnimating) return;

    const wasLiked = WishlistState.isLiked(this.productHandle);

    // Toggle the state
    const newLikedState = await WishlistState.toggle(
      this.productHandle,
      this.productId
    );

    // Animate the icon
    this.animateIcon(newLikedState);

    // Trigger fly animation when adding to wishlist (not removing)
    if (newLikedState && !wasLiked) {
      this.animateFlyToWishlist();
    }

    // State change triggers subscriber which updates count display
  }

  /**
   * Animate product image flying to the header wishlist icon
   */
  animateFlyToWishlist() {
    const wishlistIcon = document.querySelector('.header-actions__wishlist-icon');
    const image = this.productImage;

    if (!wishlistIcon || !image) return;

    const flyElement = document.createElement('fly-to-wishlist');
    flyElement.style.backgroundImage = `url('${image}')`;
    flyElement.source = this.btn || this;
    flyElement.destination = wishlistIcon;

    document.body.appendChild(flyElement);
  }

  /**
   * Update state from WishlistState
   */
  updateState() {
    if (!this.productHandle) return;

    const isLiked = WishlistState.isLiked(this.productHandle);
    this.classList.toggle("wishlist-button--active", isLiked);

    if (this.btn) {
      this.btn.setAttribute(
        "aria-label",
        isLiked ? "Remove from wishlist" : "Add to wishlist"
      );
      this.btn.setAttribute("aria-pressed", isLiked ? "true" : "false");
    }
  }

  /**
   * Update the count display based on server count and local liked state
   */
  updateCountDisplay() {
    if (!this.countEl) return;

    // Compact (grid card) buttons never show a count
    if (this.isCompact) {
      this.countEl.textContent = "";
      return;
    }

    const displayCount = this.getDisplayCount();
    const humanized = this.humanizeCount(displayCount);
    const loveText = this.hasAttribute('data-gallery') ? ` ${this.dataset.loveText || 'saved'}` : '';
    this.countEl.textContent = humanized + loveText;
  }

  /**
   * Animate the heart icon
   */
  animateIcon(isLiked) {
    if (!this.iconEl || this.isAnimating) return;

    this.isAnimating = true;

    // Add bounce animation
    this.iconEl.classList.add("wishlist-button__icon--animating");

    // Remove animation class after completion
    setTimeout(() => {
      this.iconEl.classList.remove("wishlist-button__icon--animating");
      this.isAnimating = false;
    }, 300);
  }
}

// Define the custom element
customElements.define("wishlist-button", WishlistButton);

// Delegated handler for standalone [data-pdp-wishlist] buttons (e.g. pcv2-fav in
// product-card-v2). These live inside an <a> card link, so we must stop propagation.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-pdp-wishlist]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  const handle = btn.dataset.productHandle;
  const id = btn.dataset.productId || null;
  if (handle) WishlistState.toggle(handle, id);
}, true); // capture phase so we beat the <a> click

// Visual sync: reflect the WishlistState on every [data-pdp-wishlist] button.
// Without this the user toggles wishlist but sees zero feedback on the card
// (pcv2-fav has no internal state, unlike <wishlist-button>).
function syncPdpWishlistButtons() {
  document.querySelectorAll('[data-pdp-wishlist]').forEach((btn) => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    btn.classList.toggle('is-liked', WishlistState.isLiked(handle));
    btn.setAttribute('aria-pressed', WishlistState.isLiked(handle) ? 'true' : 'false');
  });
}
document.addEventListener('wishlist:changed', syncPdpWishlistButtons);
// Hydrate initial state once the store has loaded from storage. Re-run on a
// short delay to catch buttons that mount after the initial pass (e.g. the
// recently-viewed rail fetches its cards client-side).
if (WishlistState.storageLoaded) syncPdpWishlistButtons();
else setTimeout(syncPdpWishlistButtons, 0);
setTimeout(syncPdpWishlistButtons, 400);
setTimeout(syncPdpWishlistButtons, 1200);

export default WishlistButton;
